/**
 * Debug ball in hand (DBG-002): pick up a ball and put it anywhere legal, to set up
 * and test a shot. Debug only (`?debug=1`, and from D-031 local development only);
 * there is no ball in hand in the game's rules.
 *
 * DBG-005 (owner, 2026-09-25): any ball on the table, not only the cue ball, so a shot
 * can be built exactly: move the object ball to the spot, then the cue ball behind it.
 *
 * A run where a ball was moved by hand cannot be replayed from its layout and shots,
 * so the Phase 4 server would reject it. That is right: the server replays what the
 * rules allow, and moving balls by hand is not in them (PRD N3).
 */
import type { BallId, BallSnapshot, TableGeometry } from '@potted/sim';

/**
 * The ball under a finger at `(xM, yM)`: the nearest one on the table within `grabM`,
 * or `undefined`. Nearest, so two balls close together can each be picked up.
 */
export function ballAt(
  balls: readonly BallSnapshot[],
  xM: number,
  yM: number,
  grabM: number,
): BallId | undefined {
  let best: BallId | undefined;
  let bestSq = grabM * grabM;
  for (const ball of balls) {
    if (ball.potted) continue;
    const dx = ball.xM - xM;
    const dy = ball.yM - yM;
    const sq = dx * dx + dy * dy;
    if (sq < bestSq) {
      bestSq = sq;
      best = ball.id;
    }
  }
  return best;
}

/**
 * The nearest legal spot for ball `id` to `(xM, yM)`, or `undefined` if that spot is
 * taken by another ball. Legal: wholly on the cloth and touching no other ball.
 *
 * Off the cloth is pulled back onto it, so dragging past a rail slides the ball along
 * it. The same goes for a pocket (DBG-006, owner 2026-09-25): the ball may sit anywhere
 * on the cloth right up to a pocket's mouth, and a drag into the mouth slides it along
 * the mouth line instead of stopping it. The old rule refused a circle a throat and a
 * ball wide around each pocket's centre, which at a side pocket reached well out over
 * the cloth: a ball dragged along the cushion froze for ~19 cm, then jumped, and it
 * felt like catching on a sharp edge.
 */
export function legalSpot(
  balls: readonly BallSnapshot[],
  id: BallId,
  geometry: TableGeometry,
  radiusM: number,
  xM: number,
  yM: number,
): { xM: number; yM: number } | undefined {
  const { widthM, heightM } = geometry.spec;
  const clampX = (v: number): number => Math.min(Math.max(v, radiusM), widthM - radiusM);
  const clampY = (v: number): number => Math.min(Math.max(v, radiusM), heightM - radiusM);
  let x = clampX(xM);
  let y = clampY(yM);

  /*
   * Keep the whole ball on the table side of every mouth line. A side pocket's mouth IS
   * the rail line, so the rail clamp above already does it; a corner's mouth cuts the
   * corner off diagonally, and this pushes the ball back square to that cut, so a drag
   * into the corner slides along it. Pushing out of one corner cannot push into another.
   */
  for (const pocket of geometry.pockets) {
    const past =
      (x - pocket.mouthCentre.xM) * pocket.outward.xM +
      (y - pocket.mouthCentre.yM) * pocket.outward.yM +
      radiusM;
    if (past > 0) {
      x = clampX(x - pocket.outward.xM * past);
      y = clampY(y - pocket.outward.yM * past);
    }
  }

  for (const ball of balls) {
    if (ball.id === id || ball.potted) continue;
    const dx = ball.xM - x;
    const dy = ball.yM - y;
    if (dx * dx + dy * dy < 4 * radiusM * radiusM) return undefined;
  }
  return { xM: x, yM: y };
}
